import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './DriverAssessmentPopup.css';
import sidebarBg from '../../assets/sidebarbg.jpg';
import defaultUserPhoto from '../../assets/default-user.png'; // Make sure you have a default user image
import { API_BASE_URL } from '../../api/apiClient';

// Circular progress component for assessment scores
const CircularProgress = ({ percentage, size = 115, index }) => {
  // If percentage is a string (e.g., "About to play"), show pending state
  if (typeof percentage === 'string') {
    return (
      <div className="circular-progress pending" style={{ width: size, height: size }}>
        <div className="circular-progress-text pending-text">Pending</div>
      </div>
    );
  }

  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="circular-progress-background"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className={`circular-progress-bar progress-${index}`}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
        />
      </svg>
      <div className="circular-progress-text">{percentage}%</div>
    </div>
  );
};

// Star rating component
const StarRating = ({ rating }) => {
  const stars = [];
  
  for (let i = 0; i < 5; i++) {
    if (i < rating) {
      stars.push(<span key={i} className="star filled">★</span>);
    } else {
      stars.push(<span key={i} className="star">☆</span>);
    }
  }
  
  return <div className="star-rating">{stars}</div>;
};

// Assessment Card component
const AssessmentCard = ({ assessment, index, onClick, isSelected }) => {
  // Determine if the assessment is pending/not attempted
  const isPending = typeof assessment.percentage === 'string';
  
  return (
    <div 
      className={`assessment-card ${isSelected ? 'border-2 border-blue-500' : ''}`} 
      onClick={() => !isPending && onClick && onClick(assessment)}
      style={{ cursor: !isPending ? 'pointer' : 'default' }}
    >
      <h3>Assessment {assessment.number}</h3>
      <CircularProgress 
        percentage={assessment.percentage} 
        index={index}
      />
      <StarRating rating={assessment.stars} />
    </div>
  );
};

const DriverAssessmentPopup = ({ isOpen, onClose, userPK, userName, vehicleName, vehicleId }) => {
  // Example data - now we'll only default name if not provided
  const defaultData = {
    vehicleName: 'Vehicle Name',
    score: 56,
    category: 'Good',
    assessments: [
      { number: 1, percentage: 65, stars: 3 },
      { number: 2, percentage: 75, stars: 4 },
      { number: 3, percentage: 85, stars: 4 }
    ],
    remarks: 'You are diong well\nKeep it up!',
    position: 7,
    vehicleType: 'Forklift type 1 (R)'
  };
  
  const [sessionOptions, setSessionOptions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(1);
  const [drivingScore, setDrivingScore] = useState(defaultData.score);
  const [scoreCategory, setScoreCategory] = useState(defaultData.category);
  const [assessmentData, setAssessmentData] = useState(defaultData.assessments);
  const [remarks, setRemarks] = useState(defaultData.remarks);
  const [editRemarks, setEditRemarks] = useState(defaultData.remarks);
  const [isEditingRemarks, setIsEditingRemarks] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [position, setPosition] = useState(defaultData.position);
  const [vehicleType, setVehicleType] = useState(defaultData.vehicleType);
  const [loading, setLoading] = useState(false);
  // Using the passed userName or a default if not provided
  const [displayUserName, setDisplayUserName] = useState(userName || 'Name Surname');
  const [userVehicleName, setUserVehicleName] = useState(vehicleName || defaultData.vehicleName);
  // Add state for user photo
  const [userPhoto, setUserPhoto] = useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [violationDetails, setViolationDetails] = useState([]);
  const [violationLoading, setViolationLoading] = useState(false);
  const [testTakenDate, setTestTakenDate] = useState(null);
  
  // Fetch user photo when component mounts
  useEffect(() => {
    if (isOpen && userPK) {
      fetchUserPhoto();
    }
  }, [isOpen, userPK]);
  
  // Fetch session options when component mounts
  useEffect(() => {
    if (isOpen && userPK && vehicleId) {
      fetchSessionOptions();
    }
  }, [isOpen, userPK, vehicleId]);
  
  // Fetch assessment cards and data when session changes
  useEffect(() => {
    if (isOpen && userPK && selectedSession && vehicleName) {
      // First fetch assessment cards which includes score data
      const getAssessmentCards = async () => {
        setLoading(true);
        try {
          await fetchAssessmentCards();
          // After assessment cards are loaded, then fetch additional data
          await fetchData();
        } finally {
          setLoading(false);
        }
      };
      
      getAssessmentCards();
    }
  }, [isOpen, userPK, selectedSession, vehicleId, vehicleName]);
  
  // Fetch user photo from API
  const fetchUserPhoto = async () => {
    try {
      console.log('Fetching photo for user with PK:', userPK);
      
      const response = await fetch(`${API_BASE_URL}/api/user-photo?userPK=${userPK}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('User photo not found, using default');
          setUserPhoto(null);
          return;
        }
        throw new Error('Failed to fetch user photo');
      }
      
      const data = await response.json();
      
      if (data.photo) {
        setUserPhoto(`data:image/jpeg;base64,${data.photo}`);
      }
    } catch (error) {
      console.error('Error fetching user photo:', error);
      setUserPhoto(null);
    }
  };
  
  // Fetch user-specific session options from the API
  const fetchSessionOptions = async () => {
    setLoading(true);
    try {
      console.log('Fetching session options for user with PK:', userPK);
      
      const response = await fetch(`${API_BASE_URL}/api/user-session-options?userPK=${userPK}&vehicleId=${vehicleId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch session options');
      }
      
      const data = await response.json();
      
      if (data.length > 0) {
        setSessionOptions(data);
        // Set the selected session to the first available one
        setSelectedSession(data[0].sessionNumber);
      } else {
        // If no sessions are available, use default
        setSessionOptions([{ sessionNumber: 1 }]);
        setSelectedSession(1);
      }
    } catch (error) {
      console.error('Error fetching session options:', error);
      // Fallback to default
      setSessionOptions([{ sessionNumber: 1 }]);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch assessment card data
  const fetchAssessmentCards = async () => {
    setLoading(true);
    try {
      console.log('Fetching assessment card data for user:', userPK);
      console.log('Vehicle name:', vehicleName);
      console.log('Session number:', selectedSession);

      const response = await fetch(`${API_BASE_URL}/api/vehicle-assessment-data?userPK=${userPK}&vehicleName=${encodeURIComponent(vehicleName)}&sessionNumber=${selectedSession}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch assessment card data');
      }
      
      const data = await response.json();
      console.log('Assessment card data:', data);
      
      // Map the data to our assessment data format
      if (data.attempts && data.attempts.length > 0) {
        const mappedAssessments = data.attempts.map(attempt => {
          console.log('Processing assessment attempt:', attempt);
          return {
            number: attempt.assessmentNumber,
            percentage: typeof attempt.score === 'string' ? attempt.score : attempt.score,
            stars: attempt.stars,
            status: attempt.status,
            vehicleID: attempt.vehicleID, // Important for fetching violation details
            attemptNumber: attempt.attemptNumber // Important for fetching violation details
          };
        });
        
        console.log('Mapped assessment data:', mappedAssessments);
        setAssessmentData(mappedAssessments);
        
        // Update driving score from the response if available
        if (data.violationData) {
          // Update average score
          if (typeof data.violationData.averageScore === 'number') {
            setDrivingScore(data.violationData.averageScore);
          }
          
          // Update score category directly from API response
          if (data.violationData.scoreCategory) {
            setScoreCategory(data.violationData.scoreCategory);
          } else {
            // Fallback to calculating category if not provided
            const avgScore = data.violationData.averageScore;
            if (avgScore >= 80) {
              setScoreCategory('Excellent');
            } else if (avgScore >= 60) {
              setScoreCategory('Good');
            } else if (avgScore >= 40) {
              setScoreCategory('Average');
            } else if (avgScore > 0) {
              setScoreCategory('Poor');
            } else {
              setScoreCategory('No data');
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching assessment card data:', error);
      // Fall back to default data
      setAssessmentData(defaultData.assessments);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchData = async () => {
    setLoading(true);
    
    try {
      console.log('Fetching data for user with PK:', userPK);
      console.log('Using vehicle ID:', vehicleId);
      console.log('Selected session:', selectedSession);
      
      // Make API call to get user assessment data for the selected session
      const response = await fetch(`${API_BASE_URL}/api/driver-assessment?userPK=${userPK}&vehicleId=${vehicleId}&session=${selectedSession}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch assessment data');
      }
      
      const data = await response.json();
      
      // Set user name and vehicle name from props
      setDisplayUserName(userName || 'Name Surname');
      setUserVehicleName(vehicleName || defaultData.vehicleName);
      
      // Set only position data from this API
      // (Score and category will come from fetchAssessmentCards)
      setPosition(data.position || defaultData.position);
      
      // Fetch remarks from User_Vehicle table
      const remarksResponse = await fetch(`${API_BASE_URL}/api/user-vehicle-remarks?userPK=${userPK}&vehicleId=${vehicleId}`);
      
      if (remarksResponse.ok) {
        const remarksData = await remarksResponse.json();
        setRemarks(remarksData.remarks || '');
        setEditRemarks(remarksData.remarks || '');
      } else {
        // Fall back to default if API fails
        setRemarks(defaultData.remarks);
        setEditRemarks(defaultData.remarks);
      }
      
      // Set vehicle type based on admin-selected vehicle
      if (vehicleName) {
        setVehicleType(vehicleName);
      } else {
        setVehicleType(defaultData.vehicleType);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fall back to default data for position only
      setPosition(defaultData.position);
      setRemarks(defaultData.remarks);
      setEditRemarks(defaultData.remarks);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSessionChange = (e) => {
    setSelectedSession(parseInt(e.target.value));
    // Reset selected attempt when session changes
    setSelectedAttempt(null);
    setViolationDetails([]);
    setTestTakenDate(null);
  };
  
  const handleEditClick = () => {
    setIsEditingRemarks(true);
  };
  
  const handleCancelEdit = () => {
    setIsEditingRemarks(false);
    setEditRemarks(remarks);
  };
  
  const handleRemarksChange = (e) => {
    setEditRemarks(e.target.value);
  };
  
  const handleEnterRemarks = async () => {
    try {
      setSaveSuccess(null); // Reset save status
      
      // Save remarks to backend if user is selected and vehicle is valid
      if (userPK && vehicleId) {
        // Save to User_Vehicle table
        const vehicleRemarksResponse = await fetch(`${API_BASE_URL}/api/update-vehicle-remarks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userPK,
            vehicleId,
            remarks: editRemarks
          }),
        });
        
        if (!vehicleRemarksResponse.ok) {
          throw new Error('Failed to update vehicle remarks');
        }
        
        // If assessment session is available, also save to assessment tables
        if (selectedSession) {
          const assessmentRemarksResponse = await fetch(`${API_BASE_URL}/api/update-remarks`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userPK,
              vehicleId,
              sessionNumber: selectedSession,
              remarks: editRemarks
            }),
          });
          
          if (!assessmentRemarksResponse.ok) {
            console.warn('Failed to update assessment remarks, but vehicle remarks were updated');
          }
        }
        
        // Update local state
        setRemarks(editRemarks);
        setIsEditingRemarks(false);
        setSaveSuccess(true);
        
        // Show success message for 4 seconds
        setTimeout(() => {
          setSaveSuccess(null);
        }, 4000);
      } else {
        // Just update local state if we can't save to backend
        setRemarks(editRemarks);
        setIsEditingRemarks(false);
        setSaveSuccess(false);
        console.warn('Missing required data to save remarks to backend');
        
        // Show error message
        setTimeout(() => {
          setSaveSuccess(null);
        }, 4000);
      }
    } catch (error) {
      console.error('Error saving remarks:', error);
      setSaveSuccess(false);
      
      // Show error message
      setTimeout(() => {
        setSaveSuccess(null);
      }, 4000);
    }
  };
  
  // Handler for assessment card click
  const handleAttemptSelect = (attempt) => {
    setSelectedAttempt(attempt);
  };
  
  // Fetch violation details when an attempt is selected
  useEffect(() => {
    if (selectedAttempt && userPK) {
      setViolationLoading(true);
      
      // Get properties from the selected attempt
      const assessmentNumber = selectedAttempt.number;
      // Use the attempt's vehicleID and attemptNumber if available, otherwise fall back to props
      const attemptVehicleID = selectedAttempt.vehicleID || vehicleId;
      const attemptNumber = selectedAttempt.attemptNumber || selectedSession;
      
      console.log('Fetching violation details for:', {
        userPK,
        assessmentNumber,
        attemptNumber,
        vehicleID: attemptVehicleID,
        selectedAttempt
      });
      
      fetchViolationDetails(userPK, assessmentNumber, attemptNumber, attemptVehicleID)
        .then(data => {
          console.log('Received violation details:', data);
          
          // Check if violations array exists
          if (data.violations && Array.isArray(data.violations)) {
            console.log(`Found ${data.violations.length} violations in the response`);
            setViolationDetails(data.violations);
            
            // Set test date from the dedicated property if available
            setTestTakenDate(data.testTakenDate);
          } else if (Array.isArray(data)) {
            // Legacy format - direct array response
            console.log(`Found ${data.length} violations in array format`);
            setViolationDetails(data);
            
            // Set the test taken date if available in first violation
            if (data.length > 0 && data[0].TestTaken) {
              setTestTakenDate(data[0].TestTaken);
            } else {
              setTestTakenDate(null);
            }
          } else {
            // No valid data found
            console.log('No valid violation data found in response:', data);
            setViolationDetails([]);
            setTestTakenDate(null);
          }
          
          setViolationLoading(false);
        })
        .catch(error => {
          console.error('Error fetching violation details:', error);
          setViolationDetails([]);
          setViolationLoading(false);
          setTestTakenDate(null);
        });
    } else {
      console.log('Not fetching violation details - missing required parameters:', {
        hasSelectedAttempt: !!selectedAttempt,
        hasVehicleId: !!vehicleId,
        hasUserPK: !!userPK,
        selectedSession
      });
    }
  }, [selectedAttempt, userPK, selectedSession, vehicleId]);
  
  // Function to fetch violation details from the API
  const fetchViolationDetails = async (userPK, assessmentNumber, attemptNumber, vehicleID) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/violation-details?userPK=${encodeURIComponent(userPK)}&assessmentNumber=${encodeURIComponent(assessmentNumber)}&attemptNumber=${encodeURIComponent(attemptNumber)}&vehicleID=${encodeURIComponent(vehicleID)}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch violation details');
      }
      
      const data = await response.json();
      console.log('Violation details response:', data);
      
      // Return the full response object
      return data;
    } catch (error) {
      console.error('Error fetching violation details:', error);
      throw error;
    }
  };
  
  if (!isOpen) return null;
  
  // Create popup content
  const popupContent = (
    <div className="driver-assessment-popup-overlay">
      <div className="driver-assessment-popup">
        {/* Left sidebar with user info */}
        <div className="driver-sidebar" style={{ background: `url(${sidebarBg}) no-repeat center center / cover` }}>
          <div className="user-photo">
            {userPhoto ? (
              <img src={userPhoto} alt={displayUserName} className="user-img" />
            ) : (
              <img src={defaultUserPhoto} alt="Default User" className="user-img" />
            )}
          </div>
          <div className="user-name">{displayUserName}</div>
          <div className="vehicle-name">{userVehicleName}</div>
        </div>
        
        {/* Main content area */}
        <div className="driver-assessment-body">
          <div className="driver-assessment-header">
            <div className="session-selector">
              <select value={selectedSession} onChange={handleSessionChange}>
                {sessionOptions.map((option) => (
                  <option key={option.sessionNumber} value={option.sessionNumber}>
                    Session {option.sessionNumber}
                  </option>
                ))}
              </select>
            </div>
            <div className="close-button" onClick={onClose}>×</div>
          </div>
          
          {loading ? (
            <div className="loading">Loading assessment data...</div>
          ) : (
            <div className="driver-assessment-content">
              {/* Cards row */}
              <div className="all-cards-row">
                {/* Driving Score Card */}
                <div className="driving-score-card">
                  <h3>Driving Score</h3>
                  <div className="score-value">{drivingScore}</div>
                  <div className="score-category">• Average Score</div>
                  <div className="score-category">{scoreCategory}</div>
                </div>
                
                {/* Assessment Cards Container */}
                <div className="assessment-cards-container">
                  {/* Assessment Cards */}
                  {assessmentData.map((assessment, index) => (
                    <AssessmentCard 
                      key={`assessment-${assessment.number}-${index}`}
                      assessment={assessment} 
                      index={index}
                      onClick={handleAttemptSelect}
                      isSelected={selectedAttempt && selectedAttempt.number === assessment.number}
                    />
                  ))}
                </div>
                
                {/* Admin's Remarks Card */}
                <div className="admin-remarks-card">
                  <h3>Admin's Remarks</h3>
                  {isEditingRemarks ? (
                    <div className="remarks-edit">
                      <textarea
                        value={editRemarks}
                        onChange={handleRemarksChange}
                        rows={4}
                        className="remarks-textarea"
                        placeholder="Write your remarks"
                      />
                      <div className="remarks-actions">
                        <button className="enter-btn" onClick={handleEnterRemarks}>Enter</button>
                        <button className="cancel-btn" onClick={handleCancelEdit}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="remarks-content">
                        {remarks ? (
                          remarks.split('\n').map((line, i) => (
                            <p key={i}>{line}</p>
                          ))
                        ) : (
                          <p className="no-remarks">No remarks yet</p>
                        )}
                      </div>
                      <div className="remarks-actions">
                        <button className="enter-btn" onClick={handleEditClick}>Edit</button>
                      </div>
                    </>
                  )}
                  
                  {/* Status message popup */}
                  {saveSuccess !== null && (
                    <div className={`remarks-status-popup ${saveSuccess ? 'success' : 'error'}`}>
                      {saveSuccess 
                        ? "Successful! Remarks have been added." 
                        : "Failed to add remarks. Please try again."}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Bottom row with breakdown assessment and leaderboard */}
              <div className="bottom-row">
                {/* Breakdown Assessment section - with reduced width */}
                <div className="breakdown-assessment-container">
                  <h3>{selectedAttempt ? `${vehicleType} Assessment Breakdown` : 'Breakdown Assessment'}</h3>
                  {violationLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading violation details...</span>
                    </div>
                  ) : selectedAttempt && violationDetails && Array.isArray(violationDetails) && violationDetails.length > 0 ? (
                    <div className="show-scrollbar">
                      <table className="violation-table">
                        <thead>
                          <tr>
                            <th scope="col">Violation Name</th>
                            <th scope="col" className="text-center">Mistake done</th>
                            <th scope="col" className="text-center">No. of violation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {violationDetails.map((violation, index) => {
                            const violationQuantity = violation.ViolationQuantity !== undefined ? violation.ViolationQuantity : 0;
                            return (
                              <tr key={index}>
                                <td>{violation.ViolationScreenName || 'Unknown Violation'}</td>
                                <td className="text-center">
                                  {violationQuantity <= 0 ? (
                                    <span className="flex items-center justify-center font-bold text-green-600 text-xl">
                                      ✓
                                    </span>
                                  ) : (
                                    <span className="flex items-center justify-center font-bold text-red-600 text-xl">
                                      ✗
                                    </span>
                                  )}
                                </td>
                                <td className="text-center">
                                  {violationQuantity <= 0 ? (
                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                      PASS
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                      {violationQuantity} {violationQuantity === 1 ? 'FAIL' : 'FAILS'}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="breakdown-content">
                      {!selectedAttempt ? (
                        <div className="text-center text-gray-500">
                          <p>Select an assessment card to view detailed violation data</p>
                        </div>
                      ) : violationDetails && Array.isArray(violationDetails) && violationDetails.length === 0 ? (
                        <div className="text-center text-gray-500">
                          <p>No violation data found for Assessment {selectedAttempt.number}</p>
                          <p className="text-sm mt-1">This assessment might not have any recorded violations or the data may not be available.</p>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500">
                          <p>Something went wrong while loading violation data</p>
                          <p className="text-sm mt-1">Please try selecting a different assessment or refreshing the page.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Leaderboard Position - positioned below admin remarks */}
                <div className="leaderboard-position">
                  <div className="position-label">Position in</div>
                  <div className="vehicle-type">{vehicleType}</div>
                  <div className="leaderboard-label">Leaderboard</div>
                  <div className="position-number">{position}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  // Use React Portal to render the popup directly to the body
  return ReactDOM.createPortal(
    popupContent,
    document.body
  );
};

export default DriverAssessmentPopup; 